import { Link } from "react-router-dom";
import { ArrowLeft, Cookie, Settings2 } from "lucide-react";
import PublicFooter from "@/components/PublicFooter";
import { Button } from "@/components/ui/button";
import { COMPANY, LEGAL_EFFECTIVE_DATE, LEGAL_VERSION, openCookieSettings } from "@/lib/legalVersion";

// Phase P.18 — GDPR — Cookie Policy for JobsBook.app.
// Written for compliance with the Irish ePrivacy Regulations (S.I. No. 336 of
// 2011) and the ePrivacy Directive 2002/58/EC — reflecting the DPC's guidance
// on cookies (April 2020).

const categories = [
  {
    key: "essential",
    title: "Strictly necessary",
    locked: true,
    body: (
      <>
        <p>
          These items are required for the Service to work. They cannot be switched off, and no
          consent is required because they fall under the "strictly necessary" exception of the
          Irish ePrivacy Regulations (Regulation 5(5)).
        </p>
        <ul className="mt-2 list-disc pl-5 space-y-1">
          <li><span className="mono">jb_token</span> — the JWT that keeps you signed in (localStorage, ~24 h).</li>
          <li><span className="mono">jbk:cookie-consent</span> — records your consent choice so we don't ask again (localStorage).</li>
          <li><span className="mono">jbk_staff_token</span> — the JWT for the Staff Portal PIN session (localStorage).</li>
          <li>Session identifier used by our reverse proxy for load balancing (session cookie, no personal data).</li>
        </ul>
      </>
    ),
  },
  {
    key: "functional",
    title: "Functional",
    locked: false,
    body: (
      <>
        <p>
          These remember your preferences to give you a nicer experience — the calendar view
          you last used, the density of tables, dismissed notification hints. Disabling them
          only affects UX, not access.
        </p>
        <ul className="mt-2 list-disc pl-5 space-y-1">
          <li><span className="mono">jobsbook:calendar-view</span> — last used calendar view (day/week/list).</li>
          <li><span className="mono">jobsbook:table-density</span> — compact / cosy toggle on data tables.</li>
          <li><span className="mono">jobsbook:hint-dismissed:*</span> — one-off dismissal flags per onboarding hint.</li>
        </ul>
      </>
    ),
  },
  {
    key: "analytics",
    title: "Analytics",
    locked: false,
    body: (
      <>
        <p>
          <strong>None currently in use.</strong> JobsBook.app does not run Google Analytics,
          Mixpanel, PostHog, Meta Pixel or any other analytics or advertising tracker. This
          toggle is present so we can transparently ask for consent <em>before</em> we ever
          consider adding one.
        </p>
      </>
    ),
  },
  {
    key: "third_party",
    title: "Third-party cookies you may encounter",
    locked: true,
    body: (
      <>
        <p>
          When you use certain features you interact directly with third parties who set their
          own cookies on their domain. We do not control these cookies — please review the
          relevant provider's policy:
        </p>
        <ul className="mt-2 list-disc pl-5 space-y-1">
          <li>Stripe Checkout / Customer Portal — <a className="text-accent underline" href="https://stripe.com/ie/legal/cookies-policy" target="_blank" rel="noopener noreferrer">stripe.com/ie/legal/cookies-policy</a></li>
          <li>Google Maps &amp; Places — <a className="text-accent underline" href="https://policies.google.com/technologies/cookies" target="_blank" rel="noopener noreferrer">policies.google.com/technologies/cookies</a></li>
        </ul>
      </>
    ),
  },
];

export default function Cookies() {
  return (
    <div className="min-h-screen bg-background text-foreground" data-testid="cookies-page">
      <div className="border-b border-border">
        <div className="max-w-[820px] mx-auto px-5 md:px-8 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2 text-sm font-bold" data-testid="cookies-back-home">
            <ArrowLeft className="h-4 w-4" /> Back to JobsBook
          </Link>
          <span className="mono text-[11px] uppercase tracking-[0.25em] text-muted-foreground">Legal</span>
        </div>
      </div>

      <main className="max-w-[820px] mx-auto px-5 md:px-8 py-12">
        <div className="mono text-[11px] uppercase tracking-[0.3em] text-accent">// {COMPANY.trading_name}</div>
        <h1 className="text-3xl md:text-4xl font-black tracking-tight mt-2 flex items-center gap-3">
          <Cookie className="h-8 w-8 text-accent" aria-hidden="true" /> Cookie Policy
        </h1>
        <div className="mt-2 text-sm text-muted-foreground">
          Effective: <span className="mono">{LEGAL_EFFECTIVE_DATE}</span>{" "}
          · Version <span className="mono">{LEGAL_VERSION}</span>
        </div>

        <p className="mt-4 text-sm leading-relaxed text-foreground/80">
          This Cookie Policy explains the cookies and similar technologies ("cookies") used on
          JobsBook.app. It is a companion to our{" "}
          <Link to="/privacy" className="text-accent underline">Privacy Policy</Link>. We follow the
          Irish Data Protection Commission's April 2020 guidance and the ePrivacy Regulations
          2011.
        </p>

        <div className="mt-6 flex items-center gap-3">
          <Button
            variant="outline"
            className="gap-2"
            onClick={openCookieSettings}
            data-testid="cookies-open-settings"
          >
            <Settings2 className="h-4 w-4" /> Change cookie settings
          </Button>
          <span className="text-xs text-muted-foreground">Re-opens the consent banner so you can change your choice.</span>
        </div>

        <div className="mt-8 space-y-8">
          {categories.map((c) => (
            <section key={c.key} data-testid={`cookies-section-${c.key}`}>
              <h2 className="text-lg font-extrabold tracking-tight flex items-center gap-2">
                {c.title}
                {c.locked && (
                  <span className="mono text-[10px] uppercase tracking-widest text-muted-foreground bg-secondary/60 border border-border rounded px-2 py-0.5">
                    Always on
                  </span>
                )}
              </h2>
              <div className="mt-2 text-sm leading-relaxed text-foreground/85 space-y-2">
                {c.body}
              </div>
            </section>
          ))}
        </div>

        <div className="mt-16 pt-8 border-t border-border">
          <PublicFooter tone="dark" />
        </div>
      </main>
    </div>
  );
}
